import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Input, Select, Button, Upload, message, Spin } from 'antd';
import { ArrowRightOutlined, UploadOutlined, BankOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { companiesApi, show422Error } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const { Option } = Select;

const normFile = (e) => {
  if (Array.isArray(e)) return e;
  return e?.fileList?.slice(-1);
};

const CompanyCreate = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [timezones, setTimezones] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingMeta(true);
      try {
        const res = await companiesApi.getCreateMeta();
        if (cancelled) return;
        setTimezones(res?.timezones || []);
        setCurrencies(res?.currencies || []);
        form.setFieldsValue({
          status: 'active',
          timezone: res?.timezones?.includes('Asia/Tehran') ? 'Asia/Tehran' : undefined,
          currency_id: res?.currencies?.find((c) => String(c.currency_code).toUpperCase() === String(res?.global_curreny_code || '').toUpperCase())?.id,
        });
      } catch (error) {
        if (!cancelled) message.error(error?.message || 'خطا در دریافت اطلاعات اولیه');
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form]);

  const onFinish = async (values) => {
    const fd = new FormData();
    fd.append('company_name', values.company_name?.trim() || '');
    fd.append('company_email', values.company_email?.trim() || '');
    fd.append('company_phone', values.company_phone?.trim() || '');
    fd.append('website', values.website?.trim() || '');
    fd.append('currency_id', String(values.currency_id ?? ''));
    fd.append('timezone', values.timezone || '');
    fd.append('status', values.status || 'active');
    fd.append('address', values.address?.trim() || '');
    fd.append('name', values.admin_name?.trim() || '');
    fd.append('email', values.admin_email?.trim() || '');
    const logoList = values.logo;
    const file = logoList?.[0]?.originFileObj || logoList?.[0];
    if (file && file instanceof Blob) {
      fd.append('logo', file);
    }
    setSubmitting(true);
    try {
      await companiesApi.create(fd);
      message.success('شرکت ثبت شد');
      navigate('/settings/companies');
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, form);
      else message.error(error?.message || 'خطا در ثبت شرکت');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-50 dark:bg-emerald-900/30">
                <BankOutlined className="text-xl text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl font-bold text-gray-800 dark:text-gray-100 sm:text-2xl">ایجاد شرکت جدید</h1>
                <p className="m-0 mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">اطلاعات شرکت و مدیر اولیه را ثبت کنید</p>
              </div>
            </div>
            <Button icon={<ArrowRightOutlined />} onClick={() => navigate('/settings/companies')}>
              بازگشت به لیست
            </Button>
          </div>

          <div className="rounded-b-xl bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50">
            <Card className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              {loadingMeta ? (
                <div className="py-16 text-center">
                  <Spin />
                </div>
              ) : (
                <Form form={form} layout="vertical" onFinish={onFinish} className="max-w-full">
                  <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
                    <Form.Item name="company_name" label="نام شرکت" rules={[{ required: true, message: 'نام شرکت را وارد کنید' }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item name="company_email" label="ایمیل شرکت" rules={[{ required: true, type: 'email', message: 'ایمیل معتبر وارد کنید' }]}>
                      <Input dir="ltr" />
                    </Form.Item>
                    <Form.Item name="company_phone" label="تلفن شرکت">
                      <Input dir="ltr" />
                    </Form.Item>
                    <Form.Item name="website" label="وب‌سایت">
                      <Input dir="ltr" placeholder="https://" />
                    </Form.Item>
                    <Form.Item name="currency_id" label="ارز" rules={[{ required: true, message: 'ارز را انتخاب کنید' }]}>
                      <Select showSearch optionFilterProp="children" popupClassName="custom-font" placeholder="ارز">
                        {currencies.map((c) => (
                          <Option key={c.id} value={c.id}>
                            {c.currency_symbol} — {c.currency_name} ({c.currency_code})
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="timezone" label="منطقه زمانی" rules={[{ required: true, message: 'منطقه زمانی را انتخاب کنید' }]}>
                      <Select showSearch optionFilterProp="children" popupClassName="custom-font" placeholder="timezone">
                        {timezones.map((tz) => (
                          <Option key={tz} value={tz}>
                            {tz}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="status" label="وضعیت" rules={[{ required: true }]}>
                      <Select popupClassName="custom-font">
                        <Option value="active">فعال</Option>
                        <Option value="inactive">غیرفعال</Option>
                      </Select>
                    </Form.Item>
                  </div>
                  <Form.Item name="address" label="آدرس">
                    <Input.TextArea rows={3} />
                  </Form.Item>
                  <div className="mt-2 border-t border-gray-100 pt-4 dark:border-gray-700">
                    <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-200">مدیر شرکت</h3>
                    <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
                      <Form.Item name="admin_name" label="نام مدیر" rules={[{ required: true, message: 'نام مدیر را وارد کنید' }]}>
                        <Input />
                      </Form.Item>
                      <Form.Item name="admin_email" label="ایمیل مدیر" rules={[{ required: true, type: 'email', message: 'ایمیل معتبر وارد کنید' }]}>
                        <Input dir="ltr" />
                      </Form.Item>
                    </div>
                  </div>
                  <Form.Item name="logo" label="لوگو" valuePropName="fileList" getValueFromEvent={normFile}>
                    <Upload maxCount={1} beforeUpload={() => false} accept="image/*">
                      <Button icon={<UploadOutlined />}>انتخاب فایل</Button>
                    </Upload>
                  </Form.Item>
                  <Form.Item className="mb-0">
                    <Button type="primary" htmlType="submit" loading={submitting} className={PANEL_PRIMARY_BTN_CLASS}>
                      ثبت شرکت
                    </Button>
                  </Form.Item>
                </Form>
              )}
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default CompanyCreate;
